import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Grid, Sky } from "@react-three/drei";
import { Box3, Vector3 } from "three";
import type { Group, Object3D, Mesh, DirectionalLight } from "three";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { gpsToLocal, type GpsOrigin } from "@/lib/gps-to-local";
import { buildFloorIndex, type FloorIndex } from "@/lib/model-layers";
import { parseModelHierarchy } from "@safers/model";
import { SiteModel } from "./SiteModel";
import { BaseMap } from "./BaseMap";
import { computeFootprintHull } from "./footprint";
import { DeviceMarker } from "./DeviceMarker";
import { ViewModeBar, type ViewMode } from "./ViewModeBar";
import { LayerPanel } from "./LayerPanel";
import { ViewControls } from "./ViewControls";
import { FloorVisibility } from "./FloorVisibility";
import { AzimuthTracker } from "./AzimuthTracker";
import { useViewerControls } from "./use-viewer-controls";
import type { SiteDevice } from "./device";
import { Compass, type CompassHandle } from "../Compass";

interface ThreeViewerProps {
  origin: GpsOrigin;
  devices?: SiteDevice[];
  selectedDeviceId?: string | null;
  onSelectDevice?: (device: SiteDevice) => void;
  modelUrl?: string;
  modelHierarchy?: string | null;
  showGrid?: boolean;
}

const SUN_DIR = new Vector3(0.5, 0.85, 0.4).normalize();
const MAP_RADIUS = 400;
const MAX_ZOOM_OUT = MAP_RADIUS * 1.5;
const CAMERA_FOV = 50;
const SHOW_LAYER_PANEL = false;

function frameCamera(c: OrbitControlsImpl, center: Vector3, size: Vector3, polar: number) {
  const maxDim = Math.max(size.x, size.z);
  const halfFov = (CAMERA_FOV * Math.PI) / 180 / 2;
  const r = Math.min(1500, Math.max(80, (maxDim / 2 / Math.tan(halfFov)) * 1.4));
  c.target.set(center.x, 0, center.z);
  c.object.position.set(center.x, r * Math.cos(polar), center.z + r * Math.sin(polar));
  c.update();
}

export function ThreeViewer({
  origin,
  devices = [],
  selectedDeviceId = null,
  onSelectDevice,
  modelUrl,
  modelHierarchy,
  showGrid = false,
}: ThreeViewerProps) {
  const controlsRef = useRef<OrbitControlsImpl>(null);
  const compassRef = useRef<CompassHandle>(null);
  const sunRef = useRef<DirectionalLight>(null);
  const controls = useViewerControls(controlsRef);
  const [controlsInstance, setControlsInstance] = useState<OrbitControlsImpl | null>(null);
  const setControlsRef = useCallback((c: OrbitControlsImpl | null) => {
    controlsRef.current = c;
    setControlsInstance(c);
  }, []);

  const [viewMode, setViewMode] = useState<ViewMode>("outside");
  const [hiddenBuildings, setHiddenBuildings] = useState<Set<string>>(new Set());
  const [hiddenFloors, setHiddenFloors] = useState<Set<string>>(new Set());
  const [layerOpen, setLayerOpen] = useState(false);
  const showLabels = false;
  const showScaffold = true;
  const [floorIndex, setFloorIndex] = useState<FloorIndex | null>(null);
  const [modelRoot, setModelRoot] = useState<Object3D | null>(null);
  const toggleBuilding = useCallback((code: string) => {
    setHiddenBuildings((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);
  const toggleFloor = useCallback((code: string, floorKey: string) => {
    const key = `${code}/${floorKey}`;
    setHiddenFloors((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const toggleAllLayers = useCallback(
    (show: boolean) => {
      setHiddenFloors(new Set());
      setHiddenBuildings(
        show ? new Set() : new Set((floorIndex?.buildingFloors ?? []).map((b) => b.code))
      );
    },
    [floorIndex]
  );

  const bounds = useMemo(() => {
    if (!modelRoot) return null;
    const box = new Box3().setFromObject(modelRoot);
    if (box.isEmpty()) return null;
    return {
      center: box.getCenter(new Vector3()),
      size: box.getSize(new Vector3()),
      minY: box.min.y,
    };
  }, [modelRoot]);

  const fitView = useCallback(() => {
    const c = controlsRef.current;
    if (c && bounds) frameCamera(c, bounds.center, bounds.size, 0.9);
  }, [bounds]);

  const { floorDates, minDate, maxDate } = useMemo(() => {
    const doc = parseModelHierarchy(modelHierarchy);
    const map = new Map<string, string>();
    let min: string | null = null;
    let max: string | null = null;
    for (const b of doc?.buildings ?? []) {
      for (const f of b.floors) {
        if (!f.plannedDate) continue;
        map.set(`${b.code}/${f.code}`, f.plannedDate);
        if (!min || f.plannedDate < min) min = f.plannedDate;
        if (!max || f.plannedDate > max) max = f.plannedDate;
      }
    }
    return { floorDates: map, minDate: min, maxDate: max };
  }, [modelHierarchy]);

  const effectiveViewDate = useMemo(() => {
    if (!minDate || !maxDate) return null;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
      now.getDate()
    ).padStart(2, "0")}`;
    return today < minDate ? minDate : today > maxDate ? maxDate : today;
  }, [minDate, maxDate]);

  const handleModelLoad = useCallback((root: Group) => {
    setModelRoot(root);
    root.traverse((o) => {
      const m = o as Mesh;
      if (m.isMesh) {
        m.castShadow = true;
        m.receiveShadow = true;
      }
    });
    setFloorIndex(buildFloorIndex(root));
  }, []);

  const hasModel = !!modelUrl;

  const footprintPolygon = useMemo(
    () => (modelRoot ? computeFootprintHull(modelRoot) : null),
    [modelRoot]
  );

  const ground = useMemo(() => {
    if (!bounds) return null;
    return {
      cx: bounds.center.x,
      cz: bounds.center.z,
      planeY: bounds.minY + 0.1,
      size: MAP_RADIUS * 2,
    };
  }, [bounds]);

  useEffect(() => {
    if (controlsInstance && bounds) {
      frameCamera(controlsInstance, bounds.center, bounds.size, 0.18);
    }
  }, [bounds, controlsInstance]);

  useEffect(() => {
    const light = sunRef.current;
    if (!bounds || !light) return;
    const { center } = bounds;
    const dist = MAP_RADIUS * 1.5;
    light.position.set(
      center.x + SUN_DIR.x * dist,
      center.y + SUN_DIR.y * dist,
      center.z + SUN_DIR.z * dist
    );
    light.target.position.copy(center);
    light.target.updateMatrixWorld();
    const cam = light.shadow.camera;
    const h = MAP_RADIUS;
    cam.left = -h;
    cam.right = h;
    cam.top = h;
    cam.bottom = -h;
    cam.near = 1;
    cam.far = dist * 3;
    cam.updateProjectionMatrix();
  }, [bounds]);

  return (
    <div className="relative h-full w-full">
      <Canvas
        camera={{
          position: hasModel ? [0, 400, 72] : [0, 320, 60],
          fov: 50,
          near: 0.1,
          far: 5000,
        }}
        shadows
        gl={{ antialias: true }}
      >
        <color attach="background" args={["#0a0e14"]} />
        <Sky
          sunPosition={[SUN_DIR.x * 100, SUN_DIR.y * 100, SUN_DIR.z * 100]}
          turbidity={6}
          rayleigh={1}
        />
        <hemisphereLight args={[0xbfd6ff, 0x8a7a66, 0.85]} />
        <ambientLight intensity={0.5} />
        <directionalLight
          ref={sunRef}
          castShadow
          intensity={2.6}
          color={0xfff3e0}
          shadow-mapSize={[2048, 2048]}
          shadow-bias={-0.0004}
        />

        <OrbitControls
          ref={setControlsRef}
          makeDefault
          maxDistance={MAX_ZOOM_OUT}
          maxPolarAngle={Math.PI * 0.8}
        />

        {showGrid && (
          <Grid
            args={[1000, 1000]}
            cellSize={10}
            cellThickness={0.5}
            sectionSize={100}
            sectionThickness={1}
            sectionColor="#666"
            cellColor="#2a2f3a"
            fadeDistance={800}
            fadeStrength={1}
            infiniteGrid
          />
        )}

        <BaseMap origin={origin} footprintPolygon={footprintPolygon} radius={MAP_RADIUS} />

        {ground && (
          <mesh
            rotation={[-Math.PI / 2, 0, 0]}
            position={[ground.cx, ground.planeY, ground.cz]}
            receiveShadow
          >
            <planeGeometry args={[ground.size, ground.size]} />
            <shadowMaterial transparent opacity={0.15} />
          </mesh>
        )}

        {modelUrl && (
          <Suspense fallback={null}>
            <SiteModel url={modelUrl} onLoad={handleModelLoad} />
          </Suspense>
        )}

        <FloorVisibility
          modelRoot={modelRoot}
          viewMode={viewMode}
          showScaffold={showScaffold}
          floorDates={floorDates}
          viewDate={effectiveViewDate}
          hiddenBuildings={hiddenBuildings}
          hiddenFloors={hiddenFloors}
        />

        {devices.map((device) => {
          const local = gpsToLocal(origin, device.lon, device.lat);
          return (
            <DeviceMarker
              key={device.id}
              position={[local.x, device.altitude + 0.5, local.z]}
              type={device.type}
              name={device.name}
              status={device.status}
              showLabel={showLabels}
              selected={selectedDeviceId === device.id}
              onClick={onSelectDevice ? () => onSelectDevice(device) : undefined}
            />
          );
        })}

        <AzimuthTracker compassRef={compassRef} />
      </Canvas>

      {hasModel ? (
        <ViewModeBar mode={viewMode} onModeChange={setViewMode} />
      ) : (
        <div className="absolute left-1/2 top-[4.75rem] z-40 -translate-x-1/2 rounded-md bg-black/75 px-3 py-1.5 text-[11px] shadow-lg ring-1 ring-white/10 backdrop-blur-sm">
          <span className="font-medium text-amber-400">3D 모델 미등록</span>
          <span className="ml-2 text-white/60">· 평면(스키매틱) 보기</span>
        </div>
      )}

      {SHOW_LAYER_PANEL && hasModel && (
        <LayerPanel
          buildingFloors={floorIndex?.buildingFloors ?? []}
          hiddenBuildings={hiddenBuildings}
          hiddenFloors={hiddenFloors}
          onToggleBuilding={toggleBuilding}
          onToggleFloor={toggleFloor}
          onToggleAll={toggleAllLayers}
          open={layerOpen}
          onOpenChange={setLayerOpen}
          className="absolute left-4 top-1/2 z-40 -translate-y-1/2"
        />
      )}

      <ViewControls
        onHome={fitView}
        onZoomIn={controls.zoomIn}
        onZoomOut={controls.zoomOut}
        className="absolute right-6 top-1/2 z-40 -translate-y-1/2"
      />

      <div className="absolute bottom-6 right-6 z-40">
        <Compass ref={compassRef} onResetNorth={controls.resetNorth} />
      </div>

      <div className="pointer-events-none absolute bottom-1 left-2 z-30 select-none text-[10px] text-white/35">
        © OpenStreetMap · © CARTO
      </div>
    </div>
  );
}
